import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Pagination,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  Box,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import "./TransactionChain.css";

// Custom hook to stop polling on navigation
const useStopPollingOnNavigation = (pollingIntervalRef, isPollingStarted) => {
  const location = useLocation();
  const hasStoppedPolling = useRef(false);

  useEffect(() => {
    console.log(`[Polling Hook] Checking location: ${location.pathname}, Polling active: ${!!pollingIntervalRef.current}`);

    const stopPolling = () => {
      if (
        location.pathname !== "/transaction-chain" &&
        pollingIntervalRef.current &&
        !hasStoppedPolling.current
      ) {
        console.log(`🛑 STOPPING POLLING - Navigated to: ${location.pathname}`);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingStarted.current = false;
        hasStoppedPolling.current = true;

        toast.info("Stopped monitoring transaction updates", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "toast-info",
        });
      }
    };

    // Check on every location change
    stopPolling();

    return () => {
      // Cleanup on unmount or location change
      if (pollingIntervalRef.current && !hasStoppedPolling.current) {
        console.log(`🧹 Cleaning up polling on unmount or route change to: ${location.pathname}`);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingStarted.current = false;
        hasStoppedPolling.current = true;
      }
    };
  }, [location.pathname, pollingIntervalRef, isPollingStarted]);
};

const iconDictionary = {
  "Proforma Invoice": "✨",
  "Purchase Order": "📦",
  Shipment: "🚚",
  Invoice: "💳",
  "Commercial Invoice": "💳",
  Payment: "💰",
  "Packing List": "📋",
  "Bill of Lading": "🚢",
  "Airway Bill": "✈️",
  "Goods Declaration": "📑",
  "Import General Manifest": "📊",
  "L/C Application": "🏦",
  "L/C Issuance": "📜",
  "Certificate of Origin": "🌍",
  "Inspection Certificate": "🔍",
  "Bill of Exchange": "💸",
  "L/C Settlement Document": "✅",
  "Delivery Order": "📥",
  "Bank remittance receipt": "💵",
  "FE Contract": "💱",
  "Bank Payment Approval": "✔️",
  "Consignment Agreement": "🤝",
  "Sales Report": "📈",
  "Advance Payment": "💸",
  "Letter of Credit": "🏦",
  "Open Account": "📖",
  "Documentary Collection (D/P)": "📜",
  "Documentary Collection (D/A)": "📜",
  "Import contract form": "📜",
  "Bank Contract": "🤝",
  "Consignment Basis": "📦",
  default: "📌",
};

// Load document flow from localStorage or use defaults
const loadDocumentFlow = () => {
  try {
    const stored = localStorage.getItem("paymentMethodDocuments");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error loading payment method documents:", error);
  }
  // Return default flow if nothing is stored
  return {
    "Advance Payment": [
      "Proforma Invoice",
      "Bank remittance receipt",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Goods Declaration",
      "Import General Manifest",
    ],
    "Letter of Credit": [
      "Proforma Invoice",
      "L/C Application",
      "L/C Issuance",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Certificate of Origin",
      "Inspection Certificate",
      "Bill of Lading",
      "Bill of Exchange",
      "Goods Declaration",
      "L/C Settlement Document",
    ],
    "Open Account": [
      "Proforma Invoice",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Delivery Order",
      "Goods Declaration",
    ],
    "Documentary Collection": [
      "Proforma Invoice",
      "Purchase Order",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Goods Declaration",
    ],
    "Bank Contract": [
      "Proforma Invoice",
      "FE Contract",
      "Purchase Order",
      "Shipment",
      "Commercial Invoice",
      "Packing List",
      "Bill of Lading",
      "Goods Declaration",
      "Bank Payment Approval",
      "Import contract form",
    ],
    "Consignment Basis": [
      "Proforma Invoice",
      "Consignment Agreement",
      "Packing List",
      "Commercial Invoice",
      "Bill of Lading",
      "Goods Declaration",
      "Sales Report",
    ],
  };
};

const documentKeywords = {
  "Proforma Invoice": {
    required: ["proforma", "pi"],
    optional: ["invoice", "pro forma", "performa"],
    exclusions: ["commercial", "final"],
  },
  "Purchase Order": {
    required: ["purchase", "po"],
    optional: ["order", "buy", "procurement"],
    exclusions: ["delivery"],
  },
  Shipment: {
    required: ["shipment", "ship"],
    optional: ["shipping", "dispatch"],
    exclusions: ["bill", "invoice"],
  },
  "Commercial Invoice": {
    required: ["commercial"],
    optional: ["invoice", "ci", "bill"],
    exclusions: ["proforma", "pro forma"],
  },
  Invoice: {
    required: ["invoice"],
    optional: ["bill"],
    exclusions: ["proforma", "commercial", "pro forma"],
  },
  Payment: {
    required: ["payment"],
    optional: ["pay", "remittance"],
    exclusions: ["advance", "bank", "approval"],
  },
  "Packing List": {
    required: ["packing", "pl"],
    optional: ["pack", "list"],
    exclusions: ["invoice", "bill"],
  },
  "Bill of Lading": {
    required: ["lading", "bl", "bol"],
    optional: ["bill"],
    exclusions: ["airway", "exchange"],
  },
  "Airway Bill": {
    required: ["airway", "awb"],
    optional: ["air", "bill"],
    exclusions: ["lading", "exchange"],
  },
  "Goods Declaration": {
    required: ["goods", "gd"],
    optional: ["declaration", "declare"],
    exclusions: ["import", "manifest"],
  },
  "Import General Manifest": {
    required: ["import", "manifest", "igm"],
    optional: ["general"],
    exclusions: ["contract", "form"],
  },
  "L/C Application": {
    required: ["lc", "application"],
    optional: ["letter", "credit", "apply"],
    exclusions: ["issuance", "settlement"],
  },
  "L/C Issuance": {
    required: ["lc", "issuance"],
    optional: ["letter", "credit", "issue"],
    exclusions: ["application", "settlement"],
  },
  "Certificate of Origin": {
    required: ["origin", "coo"],
    optional: ["certificate", "cert"],
    exclusions: ["inspection"],
  },
  "Inspection Certificate": {
    required: ["inspection"],
    optional: ["certificate", "inspect", "cert"],
    exclusions: ["origin"],
  },
  "Bill of Exchange": {
    required: ["exchange", "boe"],
    optional: ["bill"],
    exclusions: ["lading", "airway"],
  },
  "L/C Settlement Document": {
    required: ["lc", "settlement"],
    optional: ["letter", "credit", "settle", "document"],
    exclusions: ["application", "issuance"],
  },
  "Delivery Order": {
    required: ["delivery", "do"],
    optional: ["order", "deliver"],
    exclusions: ["purchase"],
  },
  "Bank remittance receipt": {
    required: ["bank", "remittance"],
    optional: ["receipt", "remit"],
    exclusions: ["contract", "payment", "approval"],
  },
  "FE Contract": {
    required: ["fe", "foreign", "exchange"],
    optional: ["contract"],
    exclusions: ["import", "bank"],
  },
  "Bank Payment Approval": {
    required: ["bank", "payment", "approval"],
    optional: ["approve"],
    exclusions: ["remittance", "contract", "fe"],
  },
  "Consignment Agreement": {
    required: ["consignment", "agreement"],
    optional: ["consign"],
    exclusions: ["basis"],
  },
  "Sales Report": {
    required: ["sales", "report"],
    optional: ["sale"],
    exclusions: ["consignment"],
  },
  "Advance Payment": {
    required: ["advance"],
    optional: ["payment", "prepayment", "pre"],
    exclusions: ["bank", "approval"],
  },
  "Letter of Credit": {
    required: ["letter", "credit"],
    optional: ["lc"],
    exclusions: ["application", "issuance", "settlement"],
  },
  "Open Account": {
    required: ["open", "account"],
    optional: [],
    exclusions: ["letter", "credit"],
  },
  "Documentary Collection (D/P)": {
    required: ["documentary", "dp", "d/p"],
    optional: ["collection"],
    exclusions: ["d/a", "da"],
  },
  "Documentary Collection (D/A)": {
    required: ["documentary", "da", "d/a"],
    optional: ["collection"],
    exclusions: ["d/p", "dp"],
  },
  "Import contract form": {
    required: ["import", "contract"],
    optional: ["form", "request", "application"],
    exclusions: ["fe", "foreign", "exchange", "bank"],
  },
  "Bank Contract": {
    required: ["bank", "contract"],
    optional: [],
    exclusions: ["import", "fe", "foreign", "exchange"],
  },
  "Consignment Basis": {
    required: ["consignment", "basis"],
    optional: [],
    exclusions: ["agreement"],
  },
};

// Function to calculate similarity score between two strings
const calculateSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  // Exact match
  if (s1 === s2) return 1.0;
  
  // Check if one string contains the other
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;
  
  // Calculate word-based similarity
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);
  
  let matchedWords = 0;
  const totalWords = Math.max(words1.length, words2.length);
  
  for (const word1 of words1) {
    for (const word2 of words2) {
      if (word1 === word2 || word1.includes(word2) || word2.includes(word1)) {
        matchedWords++;
        break;
      }
    }
  }
  
  return matchedWords / totalWords;
};

// Enhanced fuzzy matching function with specific hints
const fuzzyMatchDocument = (uploadedDocName, requiredDocName) => {
  const uploaded = uploadedDocName.toLowerCase().trim();
  const required = requiredDocName.toLowerCase().trim();
  
  // Direct similarity check
  const directSimilarity = calculateSimilarity(uploaded, required);
  if (directSimilarity >= 0.8) return true;
  
  // Get document-specific keywords
  const docKeywords = documentKeywords[requiredDocName];
  if (!docKeywords) return false;
  
  // Check for exclusions first - if any exclusion word is found, reject immediately
  if (docKeywords.exclusions) {
    for (const exclusion of docKeywords.exclusions) {
      if (uploaded.includes(exclusion.toLowerCase())) {
        console.log(`Document "${uploadedDocName}" excluded from "${requiredDocName}" due to exclusion word: "${exclusion}"`);
        return false;
      }
    }
  }
  
  // Check if ALL required keywords are present
  let requiredMatches = 0;
  const requiredKeywords = docKeywords.required || [];
  
  for (const reqKeyword of requiredKeywords) {
    if (uploaded.includes(reqKeyword.toLowerCase())) {
      requiredMatches++;
    }
  }
  
  // If not all required keywords are found, check if at least one required + some optional match
  if (requiredMatches === requiredKeywords.length) {
    console.log(`Document "${uploadedDocName}" matched "${requiredDocName}" - all required keywords found`);
    return true;
  }
  
  // If at least one required keyword matches, check optional keywords for better confidence
  if (requiredMatches > 0) {
    let optionalMatches = 0;
    const optionalKeywords = docKeywords.optional || [];
    
    for (const optKeyword of optionalKeywords) {
      if (uploaded.includes(optKeyword.toLowerCase())) {
        optionalMatches++;
      }
    }
    
    // Need at least one required + one optional for a match
    if (optionalMatches > 0) {
      console.log(`Document "${uploadedDocName}" matched "${requiredDocName}" - partial required (${requiredMatches}/${requiredKeywords.length}) + optional keywords`);
      return true;
    }
  }
  
  console.log(`Document "${uploadedDocName}" did not match "${requiredDocName}" - insufficient keyword matches`);
  return false;
};

const documentNameMapping = {
  Invoice: "Commercial Invoice",
};

const TransactionChainPage = () => {
  const [transactionChains, setTransactionChains] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentDocumentFlow, setCurrentDocumentFlow] = useState(loadDocumentFlow());
  const itemsPerPage = 5;
  const navigate = useNavigate();
  const location = useLocation();
  const hasMounted = useRef(false);
  const componentId = useRef(uuidv4());
  const pollingIntervalRef = useRef(null);
  const isPollingStarted = useRef(false);

  console.log(`TransactionChainPage mounted [ID: ${componentId.current}]`);

  // Add useEffect to reload document flow when window gains focus (when user returns from setup page)
  useEffect(() => {
    const handleFocus = () => {
      const updatedFlow = loadDocumentFlow();
      setCurrentDocumentFlow(updatedFlow);
      console.log("Document flow reloaded from localStorage:", updatedFlow);
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchTransactionChains = useCallback(async (isPolling = false) => {
    const token = localStorage.getItem("authToken");
    console.log(
      `Fetching transaction chains with token: ${token} from ${process.env.REACT_APP_API_BASE_URL} [ID: ${componentId.current}] [Polling: ${isPolling}]`
    );
    if (!token) {
      const errorMessage = "No authentication token found. Please log in again.";
      setError(errorMessage);
      setLoading(false);
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      navigate("/login");
      return false; // Stop polling on auth failure
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/transactions_with_chain`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log(
        `Response received [ID: ${componentId.current}]:`,
        response.data
      );
      const result = Array.isArray(response.data)
        ? response.data
        : response.data.data;

      if (!Array.isArray(result)) {
        throw new Error("Invalid API format: Expected an array");
      }

      const updatedResult = result.map(chain => {
        if (!chain.uploadedFiles && chain.chain) {
          return {
            ...chain,
            uploadedFiles: chain.chain.map(stage => stage.name),
          };
        }
        return chain;
      });

      if (isPolling) {
        // During polling, only update transactions where running_validations changed from true to false
        setTransactionChains(prevChains => {
          const updatedChains = [...prevChains];
          updatedResult.forEach(newChain => {
            const index = updatedChains.findIndex(c => c.id === newChain.id);
            if (index !== -1) {
              // Check if running_validations changed from true to false
              if (
                prevChains[index].running_validations === true &&
                newChain.running_validations === false
              ) {
                console.log(
                  `Updating transaction ${newChain.id} due to running_validations change from true to false`
                );
                updatedChains[index] = newChain;
              }
            } else {
              // New transaction, add it
              updatedChains.push(newChain);
            }
          });
          return updatedChains;
        });
      } else {
        // Initial fetch, set full data
        console.log(`Setting transactionChains:`, updatedResult);
        setTransactionChains(updatedResult);
      }

      // Check if all transactions have running_validations: false
      const allValidationsComplete = updatedResult.every(
        chain => chain.running_validations === false
      );
      setLoading(false);
      return allValidationsComplete;
    } catch (error) {
      console.error(`Axios error [ID: ${componentId.current}]:`, {
        message: error.message,
        response: error.response
          ? {
              status: error.response.status,
              data: error.response.data,
              headers: error.response.headers,
            }
          : null,
        request: error.request,
      });
      let errorMessage = "Failed to load transactions";
      if (error.response?.status === 403) {
        errorMessage = "Access denied: Invalid or expired token. Please log in again.";
        localStorage.removeItem("authToken");
        navigate("/login");
      } else if (error.response?.data?.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else {
        errorMessage += `: ${error.message}`;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "toast-error",
      });
      setLoading(false);
      return false; // Stop polling on error
    }
  }, [navigate]);

  useEffect(() => {
    if (hasMounted.current) {
      console.log(
        `Skipping initial API call due to Strict Mode remount [ID: ${componentId.current}]`
      );
      return;
    }
    hasMounted.current = true;

    // Initial fetch
    fetchTransactionChains().then(allValidationsComplete => {
      if (!allValidationsComplete && !isPollingStarted.current) {
        // Ensure any existing interval is cleared before starting a new one
        if (pollingIntervalRef.current) {
          console.log(`🧹 Clearing existing polling interval before starting new [ID: ${componentId.current}]`);
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        isPollingStarted.current = true;
        console.log(`⏰ Starting polling [ID: ${componentId.current}]`);
        // Start polling every 5 seconds
        pollingIntervalRef.current = setInterval(async () => {
          console.log(`Polling transaction chains [ID: ${componentId.current}]`);
          const allComplete = await fetchTransactionChains(true);
          if (allComplete) {
            console.log(`All validations complete, stopping polling [ID: ${componentId.current}]`);
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            isPollingStarted.current = false;
            toast.info("All validations complete.", {
              position: "bottom-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              className: "toast-info",
            });
          }
        }, 5000);
      }
    });

    // Cleanup interval on component unmount
    return () => {
      if (pollingIntervalRef.current) {
        console.log(`🧹 Cleaning up polling interval on unmount [ID: ${componentId.current}]`);
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPollingStarted.current = false;
      }
    };
  }, [fetchTransactionChains]);

  useStopPollingOnNavigation(pollingIntervalRef, isPollingStarted);

  const sortedChains = useMemo(() => {
    console.log(`Sorting chains:`, transactionChains, `Order: ${sortOrder}`);
    return [...transactionChains].sort((a, b) => {
      const dateA = a.lastUpdateDate ? new Date(a.lastUpdateDate) : new Date(0);
      const dateB = b.lastUpdateDate ? new Date(b.lastUpdateDate) : new Date(0);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });
  }, [transactionChains, sortOrder]);

  const filteredChains = useMemo(() => {
    console.log(`Filtering chains:`, sortedChains, `Search term: "${searchTerm}"`);
    return sortedChains.filter((chain) => {
      const term = searchTerm.toLowerCase();
      return (
        !searchTerm ||
        chain.trading_partners?.toLowerCase().includes(term) ||
        chain.transaction_name?.toLowerCase().includes(term)
      );
    });
  }, [searchTerm, sortedChains]);

  const paginatedChains = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginated = filteredChains.slice(startIndex, startIndex + itemsPerPage);
    console.log(`Paginated chains:`, paginated);
    return paginated;
  }, [filteredChains, currentPage]);

  const handlePageChange = useCallback((event, value) => {
    console.log(`Changing page to: ${value}`);
    setCurrentPage(value);
  }, []);

  const handleCardClick = useCallback(
    (id) => {
      console.log(`Navigating to transaction-details/${id}`);
      navigate(`/transaction-details/${id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback((event) => {
    console.log(`Changing sort order to: ${event.target.value}`);
    setSortOrder(event.target.value);
    setCurrentPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    console.log(`Retrying fetch [ID: ${componentId.current}]`);
    toast.info("Retrying to fetch transaction chains...", {
      position: "bottom-right",
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      className: "toast-info",
    });
    fetchTransactionChains();
  }, [fetchTransactionChains]);

  const getRequiredDocumentIcons = useCallback((chain) => {
    const requiredDocs = currentDocumentFlow[chain.payment_type] || [];
    const uploadedDocs = chain.chain?.map(stage => stage.name) || [];
    console.log(`Required Documents for chain ${chain.id}:`, {
      requiredDocs,
      uploadedDocsFromChain: uploadedDocs,
      uploadedFilesField: chain.uploadedFiles,
      paymentType: chain.payment_type,
    });

    return requiredDocs.map((doc, idx) => {
      // Enhanced matching logic
      const isPresent = uploadedDocs.some((uploaded) => {
        // First check the mapping
        const mappedUploaded = documentNameMapping[uploaded] || uploaded;
        
        // Exact match check
        if (mappedUploaded.toLowerCase() === doc.toLowerCase()) {
          return true;
        }
        
        // Fuzzy match check
        return fuzzyMatchDocument(mappedUploaded, doc);
      });

      const icon = iconDictionary[doc] || iconDictionary.default;
      
      console.log(`Checking doc "${doc}" for chain ${chain.id}:`, {
        isPresent,
        uploadedDocs,
        mappedDocs: uploadedDocs.map(u => documentNameMapping[u] || u),
      });
      
      return (
        <div key={idx} className={`required-doc ${!isPresent ? "missing" : ""}`} title="">
          <span className="doc-icon">{icon}</span>
          <span className="doc-name">{doc}</span>
        </div>
      );
    });
  }, [currentDocumentFlow]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleString("en-US", {
        timeZone: "Asia/Karachi",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).replace(/,/, "");
    } catch {
      return "Invalid Date";
    }
  };

  const currentDateTime = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Karachi",
    hour12: true,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Container className="transaction-container" maxWidth={false}>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <Box className="inner-transaction-container">
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            label="Search by Trading Partners or Transaction Name"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => {
              console.log(`Search term changed: "${e.target.value}"`);
              setSearchTerm(e.target.value);
            }}
            className="search-input"
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <Select value={sortOrder} onChange={handleSortChange}>
              <MenuItem value="desc">Newest First</MenuItem>
              <MenuItem value="asc">Oldest First</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Box sx={{ mb: 2, textAlign: "center" }}>
            <Typography color="error">{error}</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
              sx={{ mt: 1 }}
            >
              Retry
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Array.from({ length: itemsPerPage }).map((_, idx) => (
              <Skeleton
                key={idx}
                variant="rectangular"
                height={150}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : filteredChains.length === 0 ? (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            No transaction chains found. Try adjusting your search or clearing the search term.
          </Typography>
        ) : (
          <div className="flex-wrapper">
            {paginatedChains.map((chain, index) => {
              const formattedCreatedDate = formatDate(chain.createdDate);
              const formattedLastUpdatedDate = formatDate(chain.lastUpdateDate);
              console.log(`Rendering chain ${chain.id}:`, {
                paymentType: chain.payment_type,
                chainNames: chain.chain?.map(s => s.name),
                uploadedFiles: chain.uploadedFiles,
                createdDate: chain.createdDate,
                formattedCreatedDate: formattedCreatedDate,
                lastUpdateDate: chain.lastUpdateDate,
                formattedLastUpdatedDate: formattedLastUpdatedDate,
                runningValidations: chain.running_validations,
              });
              const paymentIcon = iconDictionary[chain.payment_type] || iconDictionary.default;
              const isDisabled = chain.running_validations === true;
              return (
                <Tooltip
                  key={chain.id || `chain-${index}`}
                  title={isDisabled ? "Validations in progress" : ""}
                  arrow
                  disableInteractive
                >
                  <Card
                    elevation={4}
                    className={`transaction-card ${isDisabled ? 'disabled' : ''}`}
                    onClick={isDisabled ? undefined : () => handleCardClick(chain.id)}
                    sx={{ 
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.6 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                      position: "relative",
                      width: "100%",
                      maxWidth: "2100px",
                      margin: "0 auto",
                    }}
                  >
                    {isDisabled && (
                      <CircularProgress
                        size={24}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                        }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="subtitle1" className="inline-info">
                        <span className="trading-partners">{chain.trading_partners || "No Partners"}</span>
                        <span className="date-field">Created: {formattedCreatedDate}</span>
                        <span className="date-field">Updated: {formattedLastUpdatedDate}</span>
                      </Typography>
                      <Typography variant="subtitle2" className="payment-type" sx={{ mt: 1 }}>
                        <span className="payment-icon" title="">
                          {paymentIcon} {chain.payment_type || "N/A"}
                        </span>
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        className="required-docs-title"
                        sx={{ mt: 2 }}
                      >
                        Required Documents
                      </Typography>
                      {chain.payment_type ? (
                        <div className="required-docs">
                          {getRequiredDocumentIcons(chain)}
                        </div>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            fontSize: "14px",
                          }}
                        >
                          No payment method specified for this transaction.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Tooltip>
              );
            })}
          </div>
        )}

        {filteredChains.length > itemsPerPage && (
          <Pagination
            count={Math.ceil(filteredChains.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            className="pagination-bar"
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          />
        )}
      </Box>
    </Container>
  );
};

export default TransactionChainPage;